import { useTranslation } from 'react-i18next';
import {
  BADGE_COLOR_HEX_MAP,
  BADGE_COLOR_PASTEL_MAP,
  BadgeColorEnum,
} from '@clickersphere/core/enums/BadgeColorEnum';
import cn from '../../../app/utils/cn';

interface IColorPaletteProps {
  value: BadgeColorEnum;
  onChange: (color: BadgeColorEnum) => void;
  disabled?: boolean;
}

const ROWS: BadgeColorEnum[][] = [
  [
    BadgeColorEnum.RED,
    BadgeColorEnum.PINK,
    BadgeColorEnum.PURPLE,
    BadgeColorEnum.INDIGO,
    BadgeColorEnum.BLUE,
  ],
  [
    BadgeColorEnum.LIGHT_BLUE,
    BadgeColorEnum.CYAN,
    BadgeColorEnum.TEAL,
    BadgeColorEnum.GREEN,
    BadgeColorEnum.LIME,
  ],
  [
    BadgeColorEnum.YELLOW,
    BadgeColorEnum.ORANGE,
    BadgeColorEnum.GRAY,
    BadgeColorEnum.BLACK,
    BadgeColorEnum.WHITE,
  ],
];

export default function ColorPalette({
  value,
  onChange,
  disabled,
}: IColorPaletteProps) {
  const { t } = useTranslation();

  function renderSwatch(color: BadgeColorEnum) {
    const isActive = value === color;
    return (
      <button
        key={color}
        type="button"
        disabled={disabled}
        aria-label={t(`badge.colors.${color}`)}
        aria-pressed={isActive}
        onClick={() => onChange(color)}
        className={cn(
          'flex h-9 flex-1 basis-0 items-center justify-center rounded-2xl p-1.5 transition-colors',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isActive
            ? 'bg-grey-100 dark:bg-grey-700'
            : 'hover:bg-grey-50 dark:hover:bg-grey-800',
        )}
      >
        <span
          className="grid h-6 w-6 place-items-center rounded-full border border-white"
          style={{ backgroundColor: BADGE_COLOR_PASTEL_MAP[color] }}
        >
          <span
            aria-hidden
            className="block h-3 w-3 rounded-full"
            style={{ backgroundColor: BADGE_COLOR_HEX_MAP[color] }}
          />
        </span>
      </button>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full max-w-[352px] flex-col items-center gap-4 rounded-2xl border bg-white p-2',
        'border-grey-100 shadow-[0_11px_20px_rgba(0,0,0,0.1)]',
        'dark:border-grey-700 dark:bg-surface-card',
      )}
    >
      {ROWS.map((row, idx) => (
        <div
          key={idx}
          className="flex w-full items-center justify-center gap-0"
        >
          {row.map(renderSwatch)}
        </div>
      ))}
    </div>
  );
}
